/**
 * CompaniesListPage — table view over vault_companies for the current
 * Holder. Mirror of fias360/AccountsListPage with sales-flow vocabulary.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsCompanies,
  useCreateFiasLeadsCompany,
  useFiasLeadsCrossArchePresence,
} from '../hooks/useFiasLeads';
import { SkeletonRow } from '../components/Skeletons';
import { CrossArcheBadge } from '../components/CrossArcheBadge';

interface Props {
  holderId: string;
}

export function CompaniesListPage({ holderId }: Props) {
  const companiesQuery = useFiasLeadsCompanies(holderId);
  const createCompany = useCreateFiasLeadsCompany(holderId);
  const presenceQuery = useFiasLeadsCrossArchePresence(
    holderId,
    [],
    (companiesQuery.data ?? []).map((c) => c.companyId),
  );
  const companyPresence = presenceQuery.data?.companyPresence ?? {};
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [industry, setIndustry] = useState('');
  const [search, setSearch] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await createCompany.mutateAsync({
      name: name.trim(),
      domain: domain.trim() || undefined,
      industry: industry.trim() || undefined,
    });
    setName('');
    setDomain('');
    setIndustry('');
    setShowCreate(false);
  };

  const filtered = useMemo(() => {
    const all = companiesQuery.data ?? [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.domain ?? '').toLowerCase().includes(q) ||
        (c.industry ?? '').toLowerCase().includes(q),
    );
  }, [companiesQuery.data, search]);

  return (
    <div className="flex flex-col gap-4 p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Companies</h1>
          <p className="text-sm text-muted-foreground">
            Organisations you sell into. Shared with Fias 360 in this workspace.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
        >
          <Plus className="h-4 w-4" />
          New company
        </button>
      </header>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="flex flex-wrap gap-2 rounded border border-border bg-card p-4"
        >
          <input
            type="text"
            placeholder="Company name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 min-w-[200px] rounded border border-border bg-background px-3 py-2"
            maxLength={200}
            required
            autoFocus
          />
          <input
            type="text"
            placeholder="Domain (acme.com)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="rounded border border-border bg-background px-3 py-2"
          />
          <input
            type="text"
            placeholder="Industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="rounded border border-border bg-background px-3 py-2"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!name.trim() || createCompany.isPending}
              className="rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
            >
              {createCompany.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          placeholder="Search companies"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded border border-border bg-card pl-10 pr-3 py-2"
        />
      </div>

      {companiesQuery.isLoading && <SkeletonRow />}
      {companiesQuery.isError && (
        <p className="text-destructive text-sm">
          Failed to load companies: {(companiesQuery.error as Error).message}
        </p>
      )}

      <div className="rounded border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Industry</th>
              <th className="text-left px-4 py-2 font-medium">Domain</th>
              <th className="text-left px-4 py-2 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((c) => (
              <tr key={c.companyId} className="hover:bg-muted/30">
                <td className="px-4 py-2">
                  <Link
                    to={`${ARCHE_PATH}/companies/${c.companyId}`}
                    className="text-foreground hover:underline"
                  >
                    {c.name}
                  </Link>
                  <CrossArcheBadge present={companyPresence[c.companyId] === true} />
                </td>
                <td className="px-4 py-2 text-muted-foreground">{c.industry ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground">{c.domain ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground text-xs">
                  {new Date(c.updatedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && !companiesQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            {search.trim()
              ? 'No companies match that search.'
              : 'No companies yet. Add your first one above.'}
          </p>
        )}
      </div>
    </div>
  );
}
